<script lang="ts">
  import Switch from "$app/components/Switch.svelte"
  import { xhr, toast } from "$app/utils"
  import Tiptap from "$app/components/Tiptap.svelte"

  interface Props {
    post_id: number
    channels: ForumChannelType[]
    status: string
    onupdate: (channel: ForumChannelType) => void
    oncreate: (channel: ForumChannelType) => void
  }

  let { post_id, channels, status = "", onupdate, oncreate }: Props = $props()

  let post = $state({}) as ForumPostType

  const fields = ["forum_channel_id", "formatted_date", "is_pinned", "content"]

  let form = {} as ForumPostType

  let errors: any = $state({})
  let saving = $state(false)

  async function savePost() {
    if (status === "edit") {
      await updatePost()
    } else {
      await createPost()
    }
  }

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      formData.append(`forum_post[${field}]`, form[field] || "")
    })
    formData.append(`forum_post[created_at]`, form["formatted_date"])

    return formData
  }

  function validForm(): boolean {
    errors = {}

    return Object.keys(errors).length === 0
  }

  function channelType(channel_type) {
    if (channel_type === "public_channel") {
      return "Public"
    } else if (channel_type === "broadcast_channel") {
      return "Public"
    } else if (channel_type === "private_channel") {
      return "Private"
    }
  }

  let loading = $state(true)
  async function loadPost(): Promise<void> {
    try {
      loading = true
      const resp = await xhr.get(`/admin/community_posts/${post_id}/edit.json`)

      post = resp.data
      setForm()
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      loading = false
    }
  }

  async function updatePost(): Promise<void> {
    if (!validForm()) return

    try {
      saving = true
      const resp = await xhr.patch(
        `/admin/community_posts/${post.id}.json`,
        getFormData()
      )

      toast("Post has been updated", "success")
      onupdate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      saving = false
    }
  }

  async function createPost(): Promise<void> {
    if (!validForm()) return
    try {
      saving = true
      const resp = await xhr.post(`/admin/community_posts.json`, getFormData())

      toast("Post has been created", "success")
      oncreate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
      console.log(errors)
    } finally {
      saving = false
    }
  }

  function setForm(): void {
    fields.forEach((field) => {
      if (["users"].includes(field)) {
        form[field] = post[field] || []
      } else {
        form[field] = post[field]
      }
    })
  }

  if (status === "edit") {
    loadPost()
  } else if (status === "new") {
    loading = false
    setForm()
  }
</script>

<form>
  <h2 class="text-base font-semibold leading-6 text-gray-900">
    {status === "edit" ? "Edit" : "New"} Post
  </h2>

  {#if loading}
    Loading
  {:else}
    <div class="flex">
      <div class="w-full px-2">
        <div class="input">
          <label for="">Author</label>
          <div class="flex space-x-3">
            <div class="flex-shrink-0">
              <img
                class="h-10 w-10 rounded-xl shadow-lg"
                src={post.user.avatar_thumb_url}
                alt={post.user.name}
              />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900">
                {post.user.name}
              </p>
              <p class="text-sm text-gray-500">
                {post.user.title}
                <span class="text-xs ml-1">
                  {#if post.plan.company_name !== "null"}
                    @ {post.plan.company_name}
                  {:else}
                    @ {post.plan.name}
                  {/if}
                </span>
              </p>
            </div>
          </div>

          <div class="input-cont">
            <label for="">Date</label>
            <input
              type="datetime-local"
              class="input"
              bind:value={form.formatted_date}
            />
          </div>

          <div class="text-red-500">
            {errors.created_at || ""}
          </div>
        </div>

        <div class="input-cont">
          <label for="">Channel</label>
          <select class="input-admin" bind:value={form.forum_channel_id}>
            {#each channels as channel}
              <option value={channel.id}
                >{channel.name} ({channelType(channel.channel_type)})</option
              >
            {/each}
          </select>
          <div class="text-red-500">
            {errors.channel_type || ""}
          </div>
        </div>

        <div class="input-cont mt-3 flex justify-start gap-x-2 items-center">
          <p class="text-sm text-gray-700">Is Pinned?</p>
          <Switch active={form.is_pinned} />
          <button
            onclick={() => (form.is_pinned = !form.is_pinned)}
            class="cursor-pointer flex items-center pt-0"
            class:text-red-500={!form.is_pinned}
            class:text-green-500={form.is_pinned}
          >
            {form.is_pinned ? "Yes" : "No"}
          </button>
        </div>
      </div>
    </div>
    <div class="mt-4 space-y-4 text-base !text-gray-800 pb-2 w-full z-0">
      <Tiptap
        id={`post-${post.id}`}
        onupdate={({ html }) => {
          form.content = html
        }}
        content={post.content}
        buttons={[
          ["bold", "italic", "underline", "strike"],
          ["bulletList", "orderedList", "blockquote"],
          ["link", "unlink", "image"]
        ]}
        allowFileUploads={true}
        allowEmojis={true}
        showSaveArrow={true}
        allowGiphy={true}
        {saving}
        onsave={savePost}
      />
    </div>

    <div class="p-4 text-center">
      <button class="btn big primary" disabled={saving} onclick={savePost}>
        {status === "edit" ? "Update" : "Create"} Post
      </button>
    </div>
  {/if}
</form>
